import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../config/axios';
import toast from 'react-hot-toast';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    const reference = searchParams.get('reference');
    const trxref = searchParams.get('trxref');
    
    if (reference || trxref) {
      verifyPayment(reference || trxref);
    } else {
      setVerifying(false);
      setPaymentStatus('error');
    }
  }, [searchParams]);

  const verifyPayment = async (reference) => {
    try {
      const response = await api.post(`/payments/verify/${reference}`);
      
      if (response.data.payment.status === 'success') {
        setPaymentStatus('success');
        setSubscription(response.data.subscription);
        toast.success('Payment successful! Your subscription has been activated.');
      } else {
        setPaymentStatus('failed');
        toast.error('Payment verification failed.');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setPaymentStatus('error');
      toast.error('Failed to verify payment. Please contact support.');
    } finally {
      setVerifying(false);
    }
  };

  const formatCurrency = (amount, currency = 'ZAR') => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying Payment</h2>
          <p className="text-gray-600">Please wait while we verify your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        {paymentStatus === 'success' ? (
          <div className="text-center">
            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-6">
              Your subscription has been activated successfully.
            </p>
            
            {subscription && (
              <div className="bg-green-50 p-4 rounded-lg mb-6 text-left">
                <h3 className="font-semibold text-green-900 mb-2">Subscription Details</h3>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Package:</span> {subscription.packageName}</p>
                  <p><span className="font-medium">Amount:</span> {formatCurrency(subscription.price, subscription.currency)}</p>
                  <p><span className="font-medium">Status:</span> 
                    <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {subscription.status}
                    </span>
                  </p>
                  <p><span className="font-medium">Start Date:</span> {formatDate(subscription.startDate)}</p>
                  <p><span className="font-medium">End Date:</span> {formatDate(subscription.endDate)}</p>
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/subscriptions')}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
              >
                View Subscriptions
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Failed</h2>
            <p className="text-gray-600 mb-6">
              {paymentStatus === 'error' 
                ? 'There was an error processing your payment. Please try again or contact support.'
                : 'Your payment could not be verified. Please contact support if you believe this is an error.'
              }
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/subscriptions')}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
